import { useState, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Button } from 'src/@/components/ui/button';
import { Input } from 'src/@/components/ui/input';
import { Label } from 'src/@/components/ui/label';
import { Alert, AlertDescription } from 'src/@/components/ui/alert';
import Iconify from 'src/components/Iconify';
import useAuth from 'src/hooks/useAuth';
import { hasFeatureAccess, FEATURE_KEYS } from 'src/utils/functions';

const platformOptions = [
    {
        id: 1,
        nameKey: 'channels.creationForm.platforms.whatsappName',
        descriptionKey: 'channels.creationForm.platforms.whatsappDescription',
        value: 'whatsapp',
        icon: 'mage:whatsapp-filled',
        disabled: false,
    },
    {
        id: 2,
        nameKey: 'channels.creationForm.platforms.whatsappBusinessName',
        descriptionKey: 'channels.creationForm.platforms.whatsappBusinessDescription',
        value: 'whatsapp_business',
        icon: 'uil:whatsapp-alt',
        disabled: false,
    },
    {
        id: 3,
        nameKey: 'channels.creationForm.platforms.instagramName',
        descriptionKey: 'channels.creationForm.platforms.instagramDescription',
        value: 'instagram',
        icon: 'mdi:instagram',
        disabled: false,
    },
    {
        id: 4,
        nameKey: 'channels.creationForm.platforms.messengerName',
        descriptionKey: 'channels.creationForm.platforms.messengerDescription',
        value: 'messenger',
        icon: 'ic:round-facebook',
        disabled: false,
    },
    {
        id: 5,
        nameKey: 'channels.creationForm.platforms.webchatName',
        descriptionKey: 'channels.creationForm.platforms.webchatDescription',
        value: 'webchat',
        icon: 'streamline-plump:web',
        disabled: false,
    },
    {
        id: 6,
        nameKey: 'channels.creationForm.platforms.telegramName',
        descriptionKey: 'channels.creationForm.platforms.telegramDescription',
        value: 'telegram',
        icon: 'logos:telegram',
        disabled: false,
    },
    {
        id: 7,
        nameKey: 'channels.creationForm.platforms.mercadoLivreName',
        descriptionKey: 'channels.creationForm.platforms.mercadoLivreDescription',
        value: 'mercado_livre',
        icon: 'arcticons:mercado-livre',
        disabled: false,
    },
];

const webhookTypeOptions = [
    {
        id: 1,
        nameKey: 'channels.creationForm.webhookTypes.laisaiV2Name',
        descriptionKey: 'channels.creationForm.webhookTypes.laisaiV2Description',
        value: 'laisai_v2',
        icon: 'mdi:robot',
    },
    {
        id: 2,
        nameKey: 'channels.creationForm.webhookTypes.directName',
        descriptionKey: 'channels.creationForm.webhookTypes.directDescription',
        value: 'direct',
        icon: 'mdi:arrow-right-bold',
    },
    {
        id: 3,
        nameKey: 'channels.creationForm.webhookTypes.dialogflowBasicName',
        descriptionKey: 'channels.creationForm.webhookTypes.dialogflowBasicDescription',
        value: 'dialogflow_basic',
        icon: 'mdi:chat-processing',
    },
    {
        id: 4,
        nameKey: 'channels.creationForm.webhookTypes.flowbuilderName',
        descriptionKey: 'channels.creationForm.webhookTypes.flowbuilderDescription',
        value: 'flowbuilder',
        icon: 'mdi:puzzle',
    },
    {
        id: 5,
        nameKey: 'channels.creationForm.webhookTypes.laisaiV3Name',
        descriptionKey: 'channels.creationForm.webhookTypes.laisaiV3Description',
        value: 'laisai_v3',
        icon: 'mdi:robot-outline',
    },
    {
        id: 6,
        nameKey: 'channels.creationForm.webhookTypes.laisAgentsName',
        descriptionKey: '',
        value: 'lais_agents',
        icon: 'mdi:robot-industrial',
    },
    {
        id: 7,
        nameKey: 'channels.creationForm.webhookTypes.apiName',
        descriptionKey: 'channels.creationForm.webhookTypes.apiDescription',
        value: 'api',
        icon: 'mdi:whatsapp',
    },
];

const OptionCard = ({ item, selected, onChange, t }) => {
    const isSelected = selected === item.value;

    return (
        <button
            type="button"
            aria-pressed={isSelected}
            className={`
                relative text-left border rounded-xl p-3.5 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED]/30
                ${isSelected
                    ? 'border-[#7C3AED] bg-violet-50 shadow-sm ring-1 ring-[#7C3AED]/10'
                    : 'border-gray-200 bg-white hover:border-violet-200 hover:bg-violet-50/30'
                }
                ${item.disabled ? 'opacity-50 cursor-not-allowed' : ''}
            `}
            onClick={() => !item.disabled && onChange(item.value)}
        >
            <div className="flex items-center space-x-3">
                <div
                    className={`
                    w-10 h-10 rounded-xl flex items-center justify-center transition-colors shrink-0
                    ${isSelected ? 'bg-white text-[#7C3AED] shadow-sm' : 'bg-gray-50 text-gray-600'}
                `}
                >
                    <Iconify
                        icon={item.icon}
                        width={20}
                        height={20}
                        className={isSelected ? 'text-[#7C3AED]' : ''}
                    />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-gray-900 leading-tight">
                        {t(item.nameKey)}
                    </div>
                    <div className="text-xs text-gray-500 line-clamp-2 mt-0.5">
                        {item.descriptionKey ? t(item.descriptionKey) : ''}
                    </div>
                </div>
                {isSelected && (
                    <div className="shrink-0">
                        <Iconify
                            icon="solar:check-circle-bold"
                            width={20}
                            height={20}
                            className="text-[#7C3AED]"
                        />
                    </div>
                )}
            </div>
        </button>
    );
};

const StepProgress = ({ step, labels }) => (
    <div className="px-6 pt-4 pb-2">
        <div className="flex items-center gap-2">
            {labels.map((label, index) => {
                const stepNumber = index + 1;
                const isActive = stepNumber === step;
                const isDone = stepNumber < step;
                return (
                    <div key={label} className="flex-1 min-w-0">
                        <div
                            className={`h-1.5 rounded-full transition-colors ${
                                isActive || isDone ? 'bg-[#7C3AED]' : 'bg-gray-200'
                            }`}
                        />
                        <p
                            className={`mt-2 text-[11px] font-medium truncate ${
                                isActive ? 'text-[#7C3AED]' : isDone ? 'text-gray-700' : 'text-gray-400'
                            }`}
                        >
                            {stepNumber}. {label}
                        </p>
                    </div>
                );
            })}
        </div>
    </div>
);

export default function ChannelCreationForm({
    onSubmit,
    onChange,
    onCancel,
    canal = {},
    isSubmitting = false,
    submitError = '',
    // Quando o chamador ja sabe a plataforma (ex.: criar canal a partir de uma
    // conta social ja conectada em Contas Sociais), pula a etapa de escolha —
    // o wizard vira nome -> direcionamento, so 2 passos.
    lockedPlatform = '',
}) {
    const { t } = useTranslation('settings');
    const { user, cliente } = useAuth();
    const steps = lockedPlatform ? ['name', 'routing'] : ['name', 'platform', 'routing'];
    const [step, setStep] = useState(1);
    const [nome, setNome] = useState(canal.nome || '');
    const [platform, setPlatform] = useState(lockedPlatform || canal.platform || '');
    const [webhookType, setWebhookType] = useState(canal.webhook_type || '');
    const currentStepKey = steps[step - 1];

    const PLATFORM_PLUGIN = {
        webchat: FEATURE_KEYS.WEBCHAT,
        messenger: FEATURE_KEYS.MESSENGER,
        instagram: FEATURE_KEYS.INSTAGRAM_DIRECT,
        telegram: FEATURE_KEYS.TELEGRAM,
        mercado_livre: FEATURE_KEYS.MERCADO_LIVRE,
    };

    const lockedPlatformOption = useMemo(
        () => (lockedPlatform ? platformOptions.find((item) => item.value === lockedPlatform) : null),
        [lockedPlatform]
    );

    const availablePlatforms = useMemo(
        () =>
            platformOptions.filter((item) => {
                const pluginKey = PLATFORM_PLUGIN[item.value];
                if (!pluginKey) return true;
                return hasFeatureAccess(cliente, user, pluginKey);
            }),
        [cliente, user]
    );

    const stepLabels = steps.map((key) => t(`channels.creationForm.steps.${key}`));

    const handleNomeChange = (event) => {
        const value = event.target.value;
        setNome(value);
        onChange({ target: { name: 'channelName', value } });
    };

    const handlePlatformChange = (value) => {
        setPlatform(value);
        onChange({ target: { name: 'channelType', value } });
    };

    const handleWebhookTypeChange = (value) => {
        setWebhookType(value);
        onChange({ target: { name: 'webhookType', value } });
    };

    const canContinue =
        (currentStepKey === 'name' && Boolean(nome.trim())) ||
        (currentStepKey === 'platform' && Boolean(platform)) ||
        (currentStepKey === 'routing' && Boolean(webhookType));

    const handleBack = () => {
        if (step === 1) {
            onCancel();
            return;
        }
        setStep((current) => current - 1);
    };

    const handleContinue = () => {
        if (!canContinue || isSubmitting) return;
        if (step < steps.length) {
            setStep((current) => current + 1);
            return;
        }
        onSubmit({
            nome: nome.trim(),
            platform,
            webhook_type: webhookType,
        });
    };

    return (
        <div className="h-full flex flex-col min-h-0">
            <StepProgress step={step} labels={stepLabels} />

            <div className="flex-1 overflow-y-auto px-6 py-5">
                {currentStepKey === 'name' && (
                    <section className="animate-in fade-in slide-in-from-right-2 duration-300">
                        <Label htmlFor="channelName" className="text-sm font-bold text-gray-900">
                            {t('channels.creationForm.nameLabel')}
                        </Label>
                        <Input
                            id="channelName"
                            value={nome}
                            onChange={handleNomeChange}
                            placeholder={t('channels.creationForm.namePlaceholder')}
                            className="mt-3 h-11 rounded-xl border-gray-200 focus-visible:ring-[#7C3AED]/20 focus-visible:border-[#7C3AED]"
                            autoFocus
                        />
                        <p className="text-xs text-gray-500 mt-2">{t('channels.creationForm.nameHint')}</p>
                        {lockedPlatform && lockedPlatformOption && (
                            <div className="mt-4 flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5">
                                <Iconify icon={lockedPlatformOption.icon} width={16} className="text-[#7C3AED]" />
                                <span className="text-xs font-medium text-gray-700">
                                    {t('channels.creationForm.lockedPlatformHint', { platform: t(lockedPlatformOption.nameKey) })}
                                </span>
                            </div>
                        )}
                    </section>
                )}

                {currentStepKey === 'platform' && (
                    <section className="animate-in fade-in slide-in-from-right-2 duration-300 space-y-3">
                        <div>
                            <Label className="text-sm font-bold text-gray-900">
                                {t('channels.creationForm.platformLabel')}
                            </Label>
                            <p className="text-xs text-gray-500 mt-1">
                                {t('channels.creationForm.platformHint')}
                            </p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {availablePlatforms.map((item) => (
                                <OptionCard
                                    key={item.id}
                                    item={item}
                                    selected={platform}
                                    onChange={handlePlatformChange}
                                    t={t}
                                />
                            ))}
                        </div>
                        {platform === 'whatsapp_business' && (
                            <Alert className="border-blue-200 bg-blue-50 rounded-xl">
                                <Iconify
                                    icon="mdi:information"
                                    width={16}
                                    height={16}
                                    className="text-blue-600"
                                />
                                <AlertDescription className="text-blue-800 text-xs leading-relaxed">
                                    {t('channels.creationForm.alerts.whatsappBusiness')}
                                </AlertDescription>
                            </Alert>
                        )}
                        {platform === 'telegram' && (
                            <Alert className="border-sky-200 bg-sky-50 rounded-xl">
                                <Iconify icon="logos:telegram" width={16} height={16} />
                                <AlertDescription className="text-sky-800 text-xs leading-relaxed">
                                    <Trans
                                        t={t}
                                        i18nKey="channels.creationForm.alerts.telegram"
                                        components={{
                                            bold: <strong />,
                                            code: <code className="bg-sky-100 px-1 rounded" />,
                                        }}
                                    />
                                </AlertDescription>
                            </Alert>
                        )}
                        {platform === 'webchat' && (
                            <Alert className="border-violet-200 bg-violet-50 rounded-xl">
                                <Iconify
                                    icon="streamline-plump:web"
                                    width={16}
                                    height={16}
                                    className="text-violet-600"
                                />
                                <AlertDescription className="text-violet-800 text-xs leading-relaxed">
                                    {t('channels.creationForm.alerts.webchat')}
                                </AlertDescription>
                            </Alert>
                        )}
                    </section>
                )}

                {currentStepKey === 'routing' && (
                    <section className="animate-in fade-in slide-in-from-right-2 duration-300 space-y-3">
                        <div>
                            <Label className="text-sm font-bold text-gray-900">
                                {t('channels.creationForm.webhookTypeLabel')}
                            </Label>
                            <p className="text-xs text-gray-500 mt-1">
                                {t('channels.creationForm.webhookTypeHint')}
                            </p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {webhookTypeOptions.map((item) => (
                                <OptionCard
                                    key={item.id}
                                    item={item}
                                    selected={webhookType}
                                    onChange={handleWebhookTypeChange}
                                    t={t}
                                />
                            ))}
                        </div>
                    </section>
                )}

                {submitError && (
                    <Alert className="mt-4 border-red-200 bg-red-50 rounded-xl">
                        <Iconify icon="solar:danger-circle-bold" width={17} className="text-red-600" />
                        <AlertDescription className="text-red-700 text-sm">{submitError}</AlertDescription>
                    </Alert>
                )}
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/80">
                <Button type="button" variant="outline" onClick={handleBack} disabled={isSubmitting}>
                    {step === 1
                        ? t('common:buttons.cancel')
                        : t('channels.creationForm.backButton')}
                </Button>
                <Button
                    type="button"
                    onClick={handleContinue}
                    disabled={!canContinue || isSubmitting}
                    className="bg-[#7C3AED] hover:bg-[#6d28d9] text-white min-w-[150px]"
                >
                    <Iconify
                        icon={
                            isSubmitting
                                ? 'eva:loader-outline'
                                : step === steps.length
                                  ? 'eva:plus-outline'
                                  : 'eva:arrow-forward-outline'
                        }
                        width={18}
                        className={isSubmitting ? 'animate-spin' : ''}
                    />
                    {isSubmitting
                        ? t('channels.creationForm.creatingButton')
                        : step === steps.length
                          ? t('channels.creationForm.submitButton')
                          : t('channels.creationForm.continueButton')}
                </Button>
            </div>
        </div>
    );
}
